import React, { useState, useRef } from "react";
import { CircularProgress, IconButton, Tooltip, Box } from "@mui/material";
import { AttachFile, FolderOpen } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  UploadDocumentDocuTypeEnum,
  UploadDocumentMetadataDto,
} from "../../api-client";
import useStore from "../../helpers/useStore";
import { StorageContext } from "../../stores/Storage/Storage.provider";
import notificationStore from "../../stores/Notification/notificationStore";
import ExistingDocumentPicker, {
  ExistingDocumentPickerRef,
} from "./ExistingDocumentPicker";

interface CopaymentExemptionUploadProps {
  patientId: number;
  exemptionId: string;
  documentId?: string | null;
  onUploadSuccess: (documentId: string, documentName: string) => void;
  excludeDocumentIds?: string[];
  disabled?: boolean;
}

const CopaymentExemptionUpload: React.FC<CopaymentExemptionUploadProps> = ({
  patientId,
  exemptionId,
  documentId,
  onUploadSuccess,
  excludeDocumentIds = [],
  disabled = false,
}) => {
  const StorageStore = useStore(StorageContext);
  const { uploadingDocument } = StorageStore;
  const [isUploading, setIsUploading] = useState(false);
  const pickerRef = useRef<ExistingDocumentPickerRef>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const metadata: UploadDocumentMetadataDto = {
        docuType: UploadDocumentDocuTypeEnum.Zuzahlungsbefreiung,
        ownerType: "Patient",
        ownerId: patientId.toString(),
        makeAccessible: true,
      };

      const response = await uploadingDocument({ ...metadata, file });

      if (response?.data?.id) {
        onUploadSuccess(response.data.id, file.name);
        notificationStore.showMessage(
          "Dokument erfolgreich hochgeladen",
          "success",
        );
      }
    } catch (error: any) {
      notificationStore.showMessage(
        error.message || "Fehler beim Hochladen des Dokuments",
        "error",
      );
    } finally {
      setIsUploading(false);
      const input = document.getElementById(
        `exemption-upload-${exemptionId}`,
      ) as HTMLInputElement;
      if (input) input.value = "";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        position: "relative",
      }}
    >
      {/* Upload new file — click directly opens file picker */}
      <input
        id={`exemption-upload-${exemptionId}`}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        style={{ display: "none" }}
        onChange={handleFileSelect}
        disabled={disabled || isUploading}
      />
      <Tooltip
        title={documentId ? "Neues Dokument hochladen" : "Dokument hochladen"}
      >
        <label htmlFor={`exemption-upload-${exemptionId}`}>
          <IconButton
            component="span"
            size="small"
            disabled={disabled || isUploading}
            color={documentId ? "secondary" : "default"}
          >
            {isUploading ? (
              <CircularProgress size={20} />
            ) : (
              <CloudUploadIcon fontSize="small" />
            )}
          </IconButton>
        </label>
      </Tooltip>

      {/* Select existing document — click directly opens dropdown */}
      <Tooltip title="Aus eAkte auswählen">
        <IconButton
          size="small"
          disabled={disabled}
          onClick={() => pickerRef.current?.open()}
        >
          <AttachFile fontSize="small" />
        </IconButton>
      </Tooltip>
      <ExistingDocumentPicker
        ref={pickerRef}
        patientId={patientId}
        onSelect={(docId, docName) => onUploadSuccess(docId, docName)}
        excludeDocumentIds={excludeDocumentIds}
        disabled={disabled}
      />
    </Box>
  );
};

export default CopaymentExemptionUpload;
